import { SearchResultData } from "../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../features/interaction_factory/InteractionFactory";
import { AnimateActorPropertiesEditor } from "../features/interaction_properties_column/editors/AnimateActorPropertiesEditor";
import AnimateActorPreview from "../features/previews/animate_actor/AnimateActorPreview";
import AnimateActorNodePreview from "../features/sequence/node_previews/AnimateActorNodePreview";
import {
  deriveAnimationId,
  isDerivableAnimationState,
} from "../helpers/AnimationIdHelper";
import { GetNewGuid } from "../helpers/GuidHelper";
import { Actor } from "./Actor";
import Interaction, { InteractionType } from "./interactions/Interaction";
import {
  compactSearchableStrings,
  SearchLookupContext,
} from "./interactions/SearchableInteractionValues";
import Take from "./Take";

export interface AnimateActorAnimation {
  /** Display label as it was when picked; may be stale after an API rename. */
  name: string;
  /** Animator state; with the interaction's `layer` it defines the animation. */
  animationState: string;
  /**
   * The API's `externalId` for this state and layer. Editor identity and the
   * key for every API call. Added lazily by `ensureAnimationIds` on load, so
   * it is only absent on references that have not been through `setTakes`.
   */
  animationId?: string;
}

export type StoredAnimateActorAnimation = AnimateActorAnimation | string;

export const defaultAnimateActorAnimation: AnimateActorAnimation = {
  name: "",
  animationState: "",
};

export function getAnimateActorAnimationName(
  animation: StoredAnimateActorAnimation | undefined
): string {
  return typeof animation === "string" ? animation : animation?.name ?? "";
}

export function getAnimateActorAnimationId(
  animation: StoredAnimateActorAnimation | undefined
): string {
  return typeof animation === "string" ? "" : animation?.animationId ?? "";
}

/**
 * Changes the interaction's layer. Because state + layer define the animation,
 * the reference's `animationId` is re-derived so it keeps addressing the pair
 * the runtime will play.
 */
export function setAnimateActorLayer(
  interaction: IAnimateActor,
  layer: number
): IAnimateActor {
  const { animation } = interaction;
  if (
    typeof animation === "string" ||
    !isDerivableAnimationState(animation?.animationState)
  ) {
    return { ...interaction, layer };
  }

  return {
    ...interaction,
    layer,
    animation: {
      ...animation,
      animationId: deriveAnimationId(animation.animationState, layer),
    },
  };
}

export function getAnimateActorAnimationState(
  animation: StoredAnimateActorAnimation | undefined
): string {
  return typeof animation === "string"
    ? animation
    : animation?.animationState || animation?.name || "";
}

export interface IAnimateActor extends Interaction {
  npcId: string;
  animation: StoredAnimateActorAnimation;
  layer: number;
  transitionTime: number;
  waitUntilFinished: boolean;
}

export const animateActorInteraction = (): InteractionFactoryEntity => {
  const view = <AnimateActorPreview />;
  const create = (getKeys: (amount: number) => string[]): IAnimateActor => {
    return {
      id: GetNewGuid(),
      name: "Animate Actor",
      type: InteractionType.AnimateActor,
      npcId: "player",
      animation: { ...defaultAnimateActorAnimation },
      layer: 0,
      transitionTime: 0.3,
      waitUntilFinished: false,
    };
  };

  const migrate = (
    from: number,
    to: number,
    interaction: Interaction
  ): IAnimateActor => {
    return interaction as IAnimateActor;
  };

  const validate = (
    interaction: Interaction,
    getValue: (key: string) => string
  ): string[] => {
    return [];
  };

  const copy = (
    interaction: Interaction,
    getKeys: (amount: number) => string[],
    replaceKey: (from: string, to: string) => void
  ): IAnimateActor => {
    return {
      ...(interaction as IAnimateActor),
      id: GetNewGuid(),
    };
  };

  const filter = (
    interaction: Interaction,
    takeName: string,
    getMatchingResults: (
      searchableStrings: string[],
      takeName: string,
      interaction: Interaction,
      filterType: string
    ) => SearchResultData[],
    _getValue: (key: string) => string,
    searchLookupContext?: SearchLookupContext,
  ): SearchResultData[] => {
    const casted = interaction as IAnimateActor;

    return getMatchingResults(
      compactSearchableStrings(
        interaction.name,
        searchLookupContext?.getActorSearchTerms(casted.npcId) ?? casted.npcId,
        getAnimateActorAnimationName(casted.animation),
        getAnimateActorAnimationState(casted.animation),
      ),
      takeName,
      interaction,
      "Animate Actor",
    );
  };

  const validateErrors = (
    interaction: Interaction,
    parentTake: Take,
    currentTake: Take,
    actorPresets: Actor[]
  ): string[] => {
    return [];
  };

  return {
    View: view,
    NodeView: (nodeId: string, interaction: Interaction) => (
      <AnimateActorNodePreview nodeId={nodeId} interaction={interaction} />
    ),
    PropertiesEditor: AnimateActorPropertiesEditor,
    FilterType: "Animate Actor",
    Create: create,
    Migrate: migrate,
    Copy: copy,
    Validate: validate,
    Filter: filter,
    ValidateErrors: validateErrors,
  };
};
